import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { pushStatus, enablePush } from "@/lib/push";

const when = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

export function AdminPushBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushStatus().then(setStatus).catch(() => setStatus("unsupported")); }, []);
  if (status !== "disabled" && status !== "denied") return null;
  const enable = async () => {
    setBusy(true);
    try { await enablePush(); toast.success("Notifiche attivate su questo dispositivo"); setStatus(await pushStatus()); }
    catch (e) { toast.error(errMsg(e)); setStatus(await pushStatus()); } finally { setBusy(false); }
  };
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#a2ca6b] bg-[#f2f7ec] p-4" data-testid="admin-push-banner">
      <p className="flex items-center gap-2 text-sm">
        {status === "denied" ? <BellOff className="h-4 w-4 shrink-0 text-red-600" /> : <Bell className="h-4 w-4 shrink-0 text-[#61a229]" />}
        {status === "denied"
          ? "Le notifiche sono bloccate su questo dispositivo: clicca sul lucchetto accanto all'indirizzo, consenti le notifiche e ricarica la pagina."
          : "Attiva le notifiche su questo dispositivo (PC o telefono) per ricevere prenotazioni, avvisi e richieste anche ad app chiusa."}
      </p>
      {status === "disabled" && (
        <Button onClick={enable} disabled={busy} className="rounded-full" data-testid="admin-push-enable">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Attiva notifiche
        </Button>
      )}
    </div>
  );
}

export function AdminBell({ dark = false }) {
  const [data, setData] = useState({ items: [], unread: [] });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const items = [...(data.unread || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
  };
  const count = data.unread?.length || 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`relative rounded-full p-2 ${dark ? "text-slate-300 hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"}`} data-testid="admin-bell">
          <Bell className="h-5 w-5" />
          {count > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white" data-testid="admin-bell-count">{count}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-96 w-80 overflow-y-auto p-0" data-testid="admin-bell-list">
        <p className="border-b px-4 py-2 text-sm font-semibold">Notifiche da leggere <span className="font-normal text-xs text-slate-400">· tocca per segnarla come letta</span></p>
        {items.length ? items.map((n, i) => (
          <button key={n.id} onClick={() => open(n)} className="block w-full border-b bg-[#f2f7ec] px-4 py-2 text-left text-sm hover:bg-slate-50" data-testid={`admin-bell-item-${i}`}>
            <p className="font-semibold">{n.title}</p>
            <p className="text-xs">{n.body}</p>
            <p className="mt-0.5 text-[10px] text-slate-400">{when(n.created_at)}</p>
          </button>
        )) : <p className="px-4 py-6 text-center text-sm text-slate-500">Nessuna notifica da leggere.</p>}
      </PopoverContent>
    </Popover>
  );
}
